import React, { useState, useEffect, useCallback } from 'react';
import { getCurrentSession } from '../services/authService';
import { createDriver, removeDriver } from '../services/adminApi';
import { cx } from '../ui/cx';
import { Card, Table, Drawer, ConfirmDialog, Field, TextInput, Button, StatusBadge, Alert } from '../ui/primitives';
import { isValidPhone, isValidEmail } from '../charter/validation';

const API_BASE = process.env.REACT_APP_MOVORRA_API_URL || 'http://localhost:8081';

const EMPTY_DRIVER = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
};

async function getAuthHeader(authSession) {
  let token = authSession?.idToken;
  try { const s = await getCurrentSession(); token = s.idToken; } catch (e) {}
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

// Payload for the admin provisioning endpoint (POST /api/admin/drivers): it mints the Cognito
// login server-side, so no userId/isActive is sent — the new driver is active by construction.
function buildDriverPayload(form) {
  return {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    email: form.email.trim(),
    phoneNumber: form.phoneNumber.trim() || null,
  };
}

// KPI card — mirrors the strip on the Dispatch board for cross-page consistency.
function Kpi({ label, value, sub, subTone }) {
  return (
    <Card className="px-4 py-3">
      <div className="text-11 font-semibold uppercase tracking-wider text-body/50">{label}</div>
      <div className="mt-1.5 text-[30px] leading-none font-bold text-primary font-mono tabular-nums tracking-tight">{value}</div>
      {sub && (
        <div className={cx('mt-1.5 text-[12px] font-medium', subTone === 'amber' ? 'text-primary' : 'text-body/55')}>
          {subTone === 'amber' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent mr-1.5 align-middle" />}
          {sub}
        </div>
      )}
    </Card>
  );
}

function OpsDrivers({ authSession }) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null); // toggle/delete failures (non-blocking)
  const [search, setSearch] = useState('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_DRIVER);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null); // driver pending delete-confirm

  // `silent` skips the full-page loading state so post-write reconciliation
  // refetches don't flash the table away.
  const fetchData = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeader(authSession);
      const driversRes = await fetch(`${API_BASE}/api/drivers`, { headers });
      if (driversRes.ok) setDrivers(await driversRes.json());
    } catch (err) {
      setError('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  }, [authSession]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => {
    setForm(EMPTY_DRIVER);
    setFormError(null);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
    setFormError(null);
  };

  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setFormError('First and last name are required');
      return;
    }
    if (!form.email.trim()) {
      setFormError('Email is required — the Cognito invite is sent there');
      return;
    }
    if (!isValidEmail(form.email)) {
      setFormError("That email doesn't look right");
      return;
    }
    if (form.phoneNumber.trim() && !isValidPhone(form.phoneNumber)) {
      setFormError('Enter a 10-digit phone number with the area code');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      // Provisions the Cognito login AND the driver row in one flow.
      await createDriver(buildDriverPayload(form), authSession);
      setDrawerOpen(false);
      // Refetch so the new row lands in the server's canonical order rather than
      // being optimistically appended to the end of the list.
      await fetchData({ silent: true });
    } catch (e) {
      setFormError(e.message || 'Failed to create driver');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (d) => {
    if (!d) return;
    setDeletingId(d.driverId);
    try {
      if (d.userId) {
        // Provisioned driver: disable the Cognito login and deactivate the row.
        await removeDriver(d.userId, authSession);
      } else {
        // Legacy driver with no linked login: fall back to the plain delete.
        const headers = await getAuthHeader(authSession);
        const res = await fetch(`${API_BASE}/api/drivers/${d.driverId}`, { method: 'DELETE', headers });
        if (!res.ok && res.status !== 204) {
          const txt = await res.text();
          throw new Error(txt || `Delete failed (${res.status})`);
        }
      }
      await fetchData({ silent: true });
    } catch (e) {
      setActionError(`Failed to remove driver: ${e.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Active/inactive toggle — flips isActive via the dedicated PATCH endpoint and
  // reconciles the list against the server.
  const handleToggleActive = async (d) => {
    setTogglingId(d.driverId);
    try {
      const headers = await getAuthHeader(authSession);
      const res = await fetch(`${API_BASE}/api/drivers/${d.driverId}/active`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ isActive: !d.isActive }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Update failed (${res.status})`);
      }
      // Reconcile against the server rather than trusting only the returned row.
      await fetchData({ silent: true });
    } catch (e) {
      setActionError(`Failed to update driver: ${e.message}`);
    } finally {
      setTogglingId(null);
    }
  };

  // Client-side search over the already-fetched list (name / email / ID).
  const filtered = drivers.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = `${d.firstName} ${d.lastName}`.toLowerCase();
    const email = (d.email || '').toLowerCase();
    const id = String(d.driverId);
    return name.includes(q) || email.includes(q) || id.includes(q);
  });

  // Roster-wide counts for the KPI strip; "Showing" reflects the active search.
  const activeAll = drivers.filter(d => d.isActive).length;
  const inactiveAll = drivers.length - activeAll;

  const columns = [
    { key: 'id', header: 'ID', mono: true, render: (d) => `#${d.driverId}` },
    { key: 'name', header: 'Name', render: (d) => `${d.firstName} ${d.lastName}` },
    { key: 'email', header: 'Email', render: (d) => d.email || '-' },
    { key: 'phone', header: 'Phone', render: (d) => d.phoneNumber || '-' },
    {
      key: 'status',
      header: 'Status',
      render: (d) => (
        <button
          type="button"
          title="Toggle active / inactive"
          disabled={togglingId === d.driverId}
          onClick={() => handleToggleActive(d)}
          className="cursor-pointer border-0 bg-transparent p-0 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <StatusBadge status={d.isActive ? 'active' : 'inactive'} />
        </button>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (d) => (
        <Button
          variant="danger"
          size="sm"
          disabled={deletingId === d.driverId}
          onClick={() => setConfirmTarget(d)}
        >
          {deletingId === d.driverId ? '...' : 'Remove'}
        </Button>
      ),
    },
  ];

  // ---- Initial states (match the Dispatch board) ----
  if (loading) {
    return (
      <div className="h-full grid place-items-center bg-surface text-body/55 text-13">
        Loading drivers…
      </div>
    );
  }
  if (error) {
    return (
      <div className="h-full grid place-items-center bg-surface p-6">
        <Alert tone="danger" title="Couldn't load drivers" className="max-w-md">
          {error}
          <div className="mt-2"><Button variant="outline" size="sm" onClick={fetchData}>Retry</Button></div>
        </Alert>
      </div>
    );
  }

  const confirmLabel = confirmTarget
    ? (`${confirmTarget.firstName} ${confirmTarget.lastName}`.trim() || `driver #${confirmTarget.driverId}`)
    : '';

  return (
    <div className="h-full min-h-0 flex flex-col gap-4 p-5 bg-surface">
      {actionError && (
        <Alert key={actionError} tone="danger" dismissible className="shrink-0">{actionError}</Alert>
      )}

      {/* header */}
      <header className="shrink-0 flex items-center gap-3">
        <div className="min-w-0">
          <h1 className="text-15 font-bold text-primary leading-tight">Drivers</h1>
          <div className="text-[12px] text-body/55 leading-tight">Manage your driver roster</div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Button variant="primary" onClick={openAdd}>+ Add driver</Button>
        </div>
      </header>

      {/* KPI strip */}
      <div className="shrink-0 grid grid-cols-4 gap-3">
        <Kpi label="Drivers" value={drivers.length} sub="in roster" />
        <Kpi label="Active" value={activeAll} sub="drivers active" />
        <Kpi label="Inactive" value={inactiveAll} sub={inactiveAll > 0 ? 'deactivated' : 'none'} subTone={inactiveAll > 0 ? 'amber' : 'muted'} />
        <Kpi label="Showing" value={filtered.length} sub={`of ${drivers.length}`} />
      </div>

      {/* toolbar */}
      <div className="shrink-0 flex items-center gap-3 flex-wrap">
        <TextInput
          className="flex-1 min-w-[240px]"
          type="text"
          placeholder="Search by name, email, or ID..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <Table className="flex-1 min-h-0" columns={columns} rows={filtered.map(d => ({ ...d, id: d.driverId }))} empty="No drivers found" />

      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
        title="Add driver"
        footer={
          <>
            <Button variant="outline" onClick={closeDrawer} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save driver'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {formError && <Alert tone="danger">{formError}</Alert>}

          <div className="grid grid-cols-2 gap-3">
            <Field label="First name *">
              <TextInput value={form.firstName} onChange={e => updateField('firstName', e.target.value)} />
            </Field>
            <Field label="Last name *">
              <TextInput value={form.lastName} onChange={e => updateField('lastName', e.target.value)} />
            </Field>
          </div>

          <Field label="Email">
            <TextInput type="email" value={form.email} onChange={e => updateField('email', e.target.value)} placeholder="driver@example.com" />
          </Field>

          <Field label="Phone">
            <TextInput value={form.phoneNumber} onChange={e => updateField('phoneNumber', e.target.value)} placeholder="+1 555 123 4567" />
          </Field>

          <div className="text-[12px] text-body/55">
            A Cognito login is created and an invite email with a temporary password is sent to the address above.
          </div>
        </div>
      </Drawer>

      <ConfirmDialog
        open={!!confirmTarget}
        onClose={() => setConfirmTarget(null)}
        onConfirm={() => handleDelete(confirmTarget)}
        title="Remove driver"
        message={confirmTarget ? `Remove ${confirmLabel}? This cannot be undone.` : ''}
        confirmLabel="Remove"
        variant="danger"
      />
    </div>
  );
}

export default OpsDrivers;
